import { Suspense, lazy, useEffect } from "react";
import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { I18nProvider } from "./i18n/I18nProvider";
import { useStartupMountSignalOnce } from "@/lib/startupMountSignal";

const AppProviders = lazy(() => import("./AppProviders"));
const Landing = lazy(() => import("./pages/Landing"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Admin = lazy(() => import("./pages/Admin"));
const PaymentReturn = lazy(() => import("./pages/PaymentReturn"));
const AppShell = lazy(() => import("./components/AppShell"));
const Partners = lazy(() => import("./pages/Partners"));
const PartnersDirectory = lazy(() => import("./pages/PartnersDirectory"));
const PartnerSignup = lazy(() => import("./pages/PartnerSignup"));
const Investors = lazy(() => import("./pages/Investors"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const Careers = lazy(() => import("./pages/Careers"));
const Shop = lazy(() => import("./pages/Shop"));

let appEnhancementsPromise: Promise<unknown> | null = null;
let appStylesPromise: Promise<unknown> | null = null;

const loadAppEnhancements = () => {
  if (!appEnhancementsPromise) {
    appEnhancementsPromise = import("./app-enhancements.css");
  }
  return appEnhancementsPromise;
};

const loadAppStyles = () => {
  if (!appStylesPromise) {
    appStylesPromise = import("./app.css");
  }
  return appStylesPromise;
};

const AppLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
    <span className="text-sm font-bold">Loading...</span>
  </div>
);

const StartupMountedRoute = ({
  children,
  label,
}: {
  children: ReactNode;
  label: string;
}) => {
  useStartupMountSignalOnce(label);
  return <>{children}</>;
};

const RoutedApp = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/") {
      return;
    }

    void loadAppStyles();
    void loadAppEnhancements();
  }, [location.pathname]);

  return (
    <Suspense fallback={<AppLoading />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/partners" element={<StartupMountedRoute label="route-partners"><Partners /></StartupMountedRoute>} />
        <Route path="/partners-directory" element={<StartupMountedRoute label="route-partners-directory"><PartnersDirectory /></StartupMountedRoute>} />
        <Route path="/partners/signup" element={<StartupMountedRoute label="route-partner-signup"><PartnerSignup /></StartupMountedRoute>} />
        <Route path="/investors" element={<StartupMountedRoute label="route-investors"><Investors /></StartupMountedRoute>} />
        <Route path="/partner-dashboard" element={<StartupMountedRoute label="route-partner-dashboard"><PartnerDashboard /></StartupMountedRoute>} />
        <Route path="/careers" element={<StartupMountedRoute label="route-careers"><Careers /></StartupMountedRoute>} />
        <Route path="/shop" element={<StartupMountedRoute label="route-shop"><Shop /></StartupMountedRoute>} />
        <Route
          path="/app/*"
          element={
            <AppProviders>
              <I18nProvider fallback={<AppLoading />}>
                <AppShell redirectPath="/app" />
              </I18nProvider>
            </AppProviders>
          }
        />
        <Route
          path="/auth"
          element={
            <StartupMountedRoute label="route-auth">
              <Navigate to="/app" replace />
            </StartupMountedRoute>
          }
        />
        <Route
          path="/recipmontelegram"
          element={
            <StartupMountedRoute label="route-telegram-legacy-exact">
              <Navigate to="/app" replace />
            </StartupMountedRoute>
          }
        />
        <Route
          path="/recipmontelegram/*"
          element={
            <StartupMountedRoute label="route-telegram-legacy">
              <Navigate to="/app" replace />
            </StartupMountedRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <StartupMountedRoute label="route-reset-password">
              <Navigate to="/app" replace />
            </StartupMountedRoute>
          }
        />
        <Route
          path="/privacy"
          element={
            <StartupMountedRoute label="route-privacy">
              <Privacy />
            </StartupMountedRoute>
          }
        />
        <Route
          path="/terms"
          element={
            <StartupMountedRoute label="route-terms">
              <Terms />
            </StartupMountedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <StartupMountedRoute label="route-admin">
              <Admin />
            </StartupMountedRoute>
          }
        />
        <Route
          path="/payment/success"
          element={
            <StartupMountedRoute label="route-payment-success">
              <PaymentReturn />
            </StartupMountedRoute>
          }
        />
        <Route
          path="/payment/cancel"
          element={
            <StartupMountedRoute label="route-payment-cancel">
              <PaymentReturn />
            </StartupMountedRoute>
          }
        />
        <Route
          path="*"
          element={
            <StartupMountedRoute label="route-not-found">
              <NotFound />
            </StartupMountedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};

const AppRouter = () => (
  <BrowserRouter>
    <RoutedApp />
  </BrowserRouter>
);

export default AppRouter;
